import { useState } from "react";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import RobotPageNav from "@/components/RobotPageNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FinanceApplicationForm } from "@/components/FinanceApplicationForm";
import mt1ReddotImg from "@/assets/robots/mt1-hero.jpeg";
import mt1AisleImg from "@/assets/robots/mt1-aisle.jpeg";

const MT1 = () => {
  const [financeOpen, setFinanceOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background">
      <SEO 
        title="PUDU MT1 - AI Powered Robotic Sweeper | Red Dot Winner 2025"
        description="Professional AI-powered robotic sweeper with 35L capacity, 70cm cleaning width, and AI trash recognition. Red Dot Design Award Winner 2025. Perfect for venues up to 100,000㎡."
        keywords="PUDU MT1, robotic sweeper, AI cleaning robot, commercial cleaning, autonomous sweeper, trash recognition, Red Dot award"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 pt-44 pb-16 md:pt-48">
        <RobotPageNav />
        
        {/* Hero Section */}
        <section className="text-center mb-16 animate-fade-in">
          <Badge className="mb-4 bg-primary text-primary-foreground">Red Dot Design Award Winner 2025</Badge>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-orbitron font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent leading-tight break-words">
            PUDU MT1
          </h1>
          <p className="text-base md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            AI Powered Robotic Sweeper - "Empty" the Facility of Any Dirt & Trash
          </p>
          
          <div className="relative max-w-4xl mx-auto mb-8 grid sm:grid-cols-2 gap-4">
            <img 
              src={mt1ReddotImg} 
              alt="PUDU MT1 AI Powered Robotic Sweeper in action" 
              className="w-full rounded-xl shadow-2xl object-cover"
            />
            <img 
              src={mt1AisleImg} 
              alt="PUDU MT1 sweeping warehouse aisle debris" 
              className="w-full rounded-xl shadow-2xl object-cover"
            />
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="glow-gold">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Request Demo
              </a>
            </Button>
            <Button size="lg" variant="outline" onClick={() => setFinanceOpen(true)}>
              Apply for Financing
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="tel:3369624539">
                Call: (336) 962-4539
              </a>
            </Button>
          </div>
        </section>

        {/* Key Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">35L</div>
            <div className="text-sm text-muted-foreground">Trashbin Capacity</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">70cm</div>
            <div className="text-sm text-muted-foreground">Practical Cleaning Width</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">100K㎡</div>
            <div className="text-sm text-muted-foreground">Max Coverage Area</div>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl font-bold text-primary mb-2">500%</div>
            <div className="text-sm text-muted-foreground">Efficiency Increase</div>
          </Card>
        </section>

        {/* Tabs */}
        <Tabs defaultValue="features" className="mb-16">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="specs">Specifications</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="features" className="space-y-8">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Core Capabilities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-xl mb-3">No Debris Too Big or Small</h3>
                  <p className="text-muted-foreground">
                    The dual-disc brushes easily handle larger debris like leaves and bottles, while efficiently sweeping up fine dust and dirt to ensure no small particles are left behind.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Active Dust Control</h3>
                  <p className="text-muted-foreground">
                    Utilizes high-flow negative pressure ventilation and an efficient filter to trap particles in the debris box, effectively preventing secondary pollution.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">AI Trash Recognition</h3>
                  <p className="text-muted-foreground">
                    Equipped with AI cameras that can recognize and identify various types of trash in real time, continually learning and evolving through deep learning and database updates.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">AI Spot Cleaning</h3>
                  <p className="text-muted-foreground">
                    Features an ultra-wide scanning field of view, effortlessly covering large areas. During routine patrols, it accurately detects and efficiently cleans up waste with 500% efficiency increase.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Advanced Technology</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Seamless Adaptation to Dynamic Environments</h3>
                  <p className="text-muted-foreground">
                    Lidar SLAM + VSLAM Integrated Positioning ensures flexible and adaptive operation, quickly adjusting to dynamic environments.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Multi-sense Safety</h3>
                  <p className="text-muted-foreground">
                    With three-dimensional depth perception and multi-sensor fusion for navigation and obstacle avoidance, the MT1 navigates complex spaces with ease.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Superior Mobility</h3>
                  <p className="text-muted-foreground">
                    Designed for effortless navigation of narrow passages with intelligent pathfinding and obstacle detection.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="specs">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Technical Specifications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Dimensions</h3>
                    <p className="text-muted-foreground">840 × 600 × 490 mm (33.1" × 23.6" × 19.3")</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Weight</h3>
                    <p className="text-muted-foreground">65 kg (143 lbs)</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Trash Bin Capacity</h3>
                    <p className="text-muted-foreground">35 L (1.2 ft³)</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Cleaning Performance</h3>
                    <p className="text-muted-foreground">
                      • Standard Mode: Max 1,800 ㎡/h (19,375 ft²/h)<br/>
                      • Spot Cleaning Mode: Max 6,000 ㎡/h (64,583 ft²/h)
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Run-time</h3>
                    <p className="text-muted-foreground">4-8 hours (varies by cleaning level)</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Charging Time</h3>
                    <p className="text-muted-foreground">Less than 3 hours</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Navigation Method</h3>
                    <p className="text-muted-foreground">VSLAM + Marker + LiDAR SLAM</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-2">Cruise Speed</h3>
                    <p className="text-muted-foreground">0.2 - 1.2 m/s (0.66 - 3.94 ft/s) adjustable</p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="applications">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Ideal Applications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-primary/20 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">Large Venues</h3>
                  <p className="text-muted-foreground mb-4">
                    Designed to tackle areas up to 100,000 square meters with professional-grade dry cleaning capabilities.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Shopping malls and retail centers</li>
                    <li>✓ Convention centers and exhibition halls</li>
                    <li>✓ Transportation hubs (airports, stations)</li>
                    <li>✓ Office buildings and corporate campuses</li>
                  </ul>
                </div>
                <div className="border border-primary/20 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">24/7 Operations</h3>
                  <p className="text-muted-foreground mb-4">
                    Continuous operation capability ensures facilities stay clean around the clock without interruption.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Warehouses and logistics centers</li>
                    <li>✓ Manufacturing facilities</li>
                    <li>✓ Hotels and hospitality venues</li>
                    <li>✓ Healthcare facilities</li>
                  </ul>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <section className="text-center">
          <Card className="p-12 glass-effect border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Experience the Future of Autonomous Cleaning</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              See how the PUDU MT1 can revolutionize your facility's cleaning operations with AI-powered efficiency.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="glow-gold">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Schedule Demo
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:3369624539">
                  Call: (336) 962-4539
                </a>
              </Button>
            </div>
          </Card>
        </section>
      </main>

        <Footer />
    </div>
  );
};

export default MT1;
